import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { BusinessesApiService } from '../core/businesses-api.service';
import { OrganizationsApiService } from '../core/organizations-api.service';
import { UsersApiService } from '../core/users-api.service';
import { Organization } from '../core/models';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.html',
  styleUrls: ['./dashboard.scss'],
  imports: [
    CommonModule,
    RouterModule,
    MatToolbarModule,
    MatCardModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatButtonModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent {
  private organizationsApi = inject(OrganizationsApiService);
  private businessesApi = inject(BusinessesApiService);
  private usersApi = inject(UsersApiService);

  loading = signal(true);
  orgs = signal<Organization[]>([]);
  displayedColumns = ['name', 'status', 'createdAt', 'actions'];
  metrics = signal({ orgs: 0, accounts: 0, users: 0 });

  constructor() {
    this.load();
  }

  async load() {
    this.loading.set(true);
    try {
      const orgs = await firstValueFrom(this.organizationsApi.list()).catch(() => []);
      const accounts = await firstValueFrom(this.businessesApi.list()).catch(() => []);
      const users = await firstValueFrom(this.usersApi.list()).catch(() => []);

      this.orgs.set(orgs || []);
      this.metrics.set({
        orgs: (orgs || []).length,
        accounts: (accounts || []).length,
        users: (users || []).length,
      });
    } catch (e) {
      console.error('Failed loading dashboard data', e);
      // fallback sample data
      const sample = [
        { name: 'Acme Corp', status: 'Active', createdAt: new Date().toISOString() },
        { name: 'Beta LLC', status: 'Pending', createdAt: new Date().toISOString() },
      ];
      this.orgs.set(sample as Organization[]);
      this.metrics.set({ orgs: sample.length, accounts: 12, users: 18 });
    } finally {
      this.loading.set(false);
    }
  }

  format(dateStr: string) {
    try {
      return formatDate(dateStr, 'medium', 'en-US');
    } catch {
      return dateStr;
    }
  }
}
